// VoluntaryBurnPanel — holders burn their own R₿L (transfer to 0x…dEaD) to
// support the ecosystem. Includes the public "Hall of Flame" leaderboard.
// Self-contained wagmi providers (same pattern as DepositForm).
import React, { useCallback, useEffect, useState } from "react";
import { WagmiProvider, useAccount, useConnect, useDisconnect, useSwitchChain, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { parseUnits, formatUnits } from "viem";
import { Flame, Wallet, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Trophy } from "lucide-react";
import { wagmiConfig, BASE_CHAIN_ID } from "../../web3/wagmiConfig";
import { RBL_CONTRACT, rblTransferAbi } from "../../web3/rblPay";
import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tBurn } from "../../i18n/voluntaryBurn";

const BURN_ADDRESS = "0x000000000000000000000000000000000000dEaD";
const MIN_BURN = 1000;
const fmt = (n) => Number(n).toLocaleString();
const short = (w) => `${w.slice(0, 6)}…${w.slice(-4)}`;

const BADGES = {
  dragon: { emoji: "🐉", key: "badge_dragon", cls: "border-fuchsia-400/50 bg-fuchsia-400/10 text-fuchsia-300" },
  flame: { emoji: "🔥", key: "badge_flame", cls: "border-orange-400/50 bg-orange-400/10 text-orange-300" },
  spark: { emoji: "⚡", key: "badge_spark", cls: "border-yellow-400/50 bg-yellow-400/10 text-yellow-300" },
};

// --- Hall of Flame ----------------------------------------------------------
const HallOfFlame = ({ board, t }) => (
  <div className="mt-8 border-t border-orange-400/15 pt-6" data-testid="hall-of-flame">
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-300/80">
      <Trophy className="mr-1.5 inline h-3.5 w-3.5" /> {t("hallOfFlame")}
    </p>
    <p className="mt-0.5 text-[11px] text-white/40">{t("hallSubtitle")}</p>

    <div className="mt-4 grid grid-cols-3 gap-3" data-testid="burn-global-stats">
      {[
        [t("totalBurned"), board ? `${fmt(Number(board.totalVoluntaryBurned))} R₿L` : "…"],
        [t("burners"), board ? fmt(board.burnersCount) : "…"],
        [t("thisMonth"), board ? `${fmt(Number(board.burnedThisMonth))} R₿L` : "…"],
      ].map(([l, v]) => (
        <div key={l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">{l}</p>
          <p className="mt-0.5 font-mono text-sm font-black text-white">{v}</p>
        </div>
      ))}
    </div>

    {(!board || board.leaders.length === 0) ? (
      <p className="mt-4 text-xs text-white/35" data-testid="burn-no-leaders">{t("noBurnersYet")}</p>
    ) : (
      <ul className="mt-4 space-y-2" data-testid="burn-leaderboard">
        {board.leaders.map((l) => {
          const b = BADGES[l.badge];
          return (
            <li key={l.wallet_address} data-testid={`burn-leader-${l.rank}`}
              className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <span className="w-7 font-mono text-xs font-black text-white/45">#{l.rank}</span>
              <span className="font-mono text-xs text-white/75">{short(l.wallet_address)}</span>
              {b && (
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] font-black uppercase tracking-wider ${b.cls}`}>
                  {b.emoji} {t(b.key)}
                </span>
              )}
              <span className="ml-auto font-mono text-xs font-black text-orange-300">{fmt(l.total_burned)} R₿L</span>
              <span className="font-mono text-[10px] text-white/30">{l.burn_count} {t("burnsLabel")}</span>
              <a href={`https://basescan.org/tx/${l.tx_hashes[l.tx_hashes.length - 1]}`} target="_blank" rel="noreferrer"
                className="text-white/30 transition-colors hover:text-orange-300" data-testid={`burn-leader-tx-${l.rank}`}>
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

// --- Confirm modal -----------------------------------------------------------
const ConfirmBurnModal = ({ amount, usd, t, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={onCancel} data-testid="burn-confirm-overlay">
    <div className="w-full max-w-md rounded-3xl border border-orange-400/30 bg-[#0B0F19] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="burn-confirm-modal">
      <p className="flex items-center gap-2 font-mono text-xs font-black uppercase tracking-[0.2em] text-orange-300">
        <AlertTriangle className="h-4 w-4" /> {t("confirmTitle")}
      </p>
      <p className="mt-4 text-sm text-white/70">{t("willBurn")}:</p>
      <p className="font-mono text-2xl font-black text-white" data-testid="burn-confirm-amount">{fmt(amount)} R₿L</p>
      {usd != null && <p className="text-xs text-white/45">{t("approxValue")}: ≈ ${usd} USD</p>}
      <p className="mt-3 text-xs text-white/55">{t("sentTo")}:</p>
      <p className="break-all font-mono text-[10px] text-orange-300/80">{BURN_ADDRESS}</p>
      <p className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[11px] font-bold text-rose-300">
        {t("permanentNote")}
      </p>
      <p className="mt-3 text-center text-sm font-black text-white">{t("areYouSure")}</p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button onClick={onCancel} data-testid="burn-confirm-cancel"
          className="rounded-xl border border-white/15 py-3 text-xs font-black uppercase tracking-[0.18em] text-white/70 hover:bg-white/[0.06]">
          {t("cancel")}
        </button>
        <button onClick={onConfirm} data-testid="burn-confirm-submit"
          className="rounded-xl bg-gradient-to-r from-orange-400 to-rose-500 py-3 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:-translate-y-0.5">
          🔥 {t("confirm")}
        </button>
      </div>
    </div>
  </div>
);

// --- Main panel ---------------------------------------------------------------
function BurnInner() {
  const { lang } = useLanguage();
  const t = (k) => tBurn(lang, k);
  const { user, token, openLogin } = useAuth();
  const rootRef = React.useRef(null);

  // Support direct deep-link /vault#burn (e.g. from the mobile burger menu).
  useEffect(() => {
    if (window.location.hash !== "#burn") return;
    const id = setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    return () => clearTimeout(id);
  }, []);

  const { address, isConnected, chainId: walletChainId } = useAccount();
  const { connectors, connect, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching } = useSwitchChain();
  const wrongNetwork = isConnected && walletChainId !== BASE_CHAIN_ID;

  const [amount, setAmount] = useState("");
  const [price, setPrice] = useState(null);
  const [board, setBoard] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [recordError, setRecordError] = useState(null);
  const [done, setDone] = useState(null); // { amount, txHash, rank }
  const [isRecording, setIsRecording] = useState(false);

  const { data: balance, refetch: refetchBalance } = useReadContract({
    address: RBL_CONTRACT, abi: rblTransferAbi, functionName: "balanceOf",
    args: [address], chainId: BASE_CHAIN_ID,
    query: { enabled: isConnected && !!address, refetchInterval: 20_000 },
  });
  const balanceRbl = balance != null ? Math.floor(Number(formatUnits(balance, 18))) : null;

  const { data: txHash, isPending: isWalletConfirm, writeContract, error: txError, reset: resetTx } = useWriteContract();
  const { isLoading: isMining, isSuccess: isMined } = useWaitForTransactionReceipt({ hash: txHash, chainId: BASE_CHAIN_ID });

  const loadBoard = useCallback(() => {
    const q = address ? `?wallet=${address}` : "";
    fetch(`${API}/burn/leaderboard${q}`).then((r) => r.json()).then(setBoard).catch(() => {});
  }, [address]);

  useEffect(() => {
    loadBoard();
    fetch(`${API}/shadow/tokens/rbl-price`).then((r) => r.json()).then(setPrice).catch(() => {});
  }, [loadBoard]);

  // After the burn TX is mined → record it server-side (verified on-chain).
  useEffect(() => {
    if (!isMined || !txHash || done || isRecording) return;
    setIsRecording(true);
    fetch(`${API}/burn/voluntary`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ wallet_address: address, tx_hash: txHash }),
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d?.detail?.error || "record_failed");
        setDone({ amount: d.amount_rbl, txHash, rank: d.new_rank });
        setAmount("");
        loadBoard();
        refetchBalance();
      })
      .catch(() => setRecordError(t("recordFailed")))
      .finally(() => setIsRecording(false));
  }, [isMined, txHash]); // eslint-disable-line react-hooks/exhaustive-deps

  const amt = Math.floor(Number(amount) || 0);
  const usd = price?.price_usd != null && amt > 0 ? (amt * price.price_usd).toFixed(5) : null;
  const insufficient = balanceRbl != null && amt > balanceRbl;
  const canBurn = user && isConnected && !wrongNetwork && amt >= MIN_BURN && !insufficient && !isWalletConfirm && !isMining && !isRecording;

  const executeBurn = () => {
    setConfirming(false);
    setRecordError(null);
    writeContract({
      address: RBL_CONTRACT, abi: rblTransferAbi, functionName: "transfer",
      args: [BURN_ADDRESS, parseUnits(String(amt), 18)], chainId: BASE_CHAIN_ID,
    });
  };

  const busyLabel = isWalletConfirm ? t("confirmInWallet") : isMining ? t("burning") : isRecording ? t("recording") : null;

  return (
    <div id="burn" ref={rootRef} className="scroll-mt-24 rounded-2xl border border-orange-400/25 bg-orange-400/[0.03] p-5" data-testid="voluntary-burn-panel">
      <p className="flex items-center gap-2 font-mono text-xs font-black uppercase tracking-[0.22em] text-orange-300">
        <Flame className="h-4 w-4" /> {t("title")}
      </p>
      <p className="mt-0.5 text-[11px] text-white/45">{t("subtitle")}</p>

      {done ? (
        <div className="mt-5 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.05] p-5 text-center" data-testid="burn-success">
          <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
          <p className="mt-2 text-sm font-black text-white">{t("thankYou")}</p>
          <p className="mt-1 font-mono text-lg font-black text-orange-300">🔥 {fmt(done.amount)} R₿L</p>
          {done.rank && <p className="text-xs text-white/55">{t("rank")}: #{done.rank}</p>}
          <a href={`https://basescan.org/tx/${done.txHash}`} target="_blank" rel="noreferrer" data-testid="burn-success-tx"
            className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] text-emerald-300 hover:underline">
            {t("viewTx")} <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              t("shareText").replace("{amount}", fmt(done.amount))
            )}&url=${encodeURIComponent("https://rebelbtc.com/vault")}`}
            target="_blank" rel="noreferrer" data-testid="burn-share-x"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-black py-2.5 text-xs font-black uppercase tracking-[0.18em] text-white transition-all hover:-translate-y-0.5 hover:border-white/50"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            {t("shareOnX")}
          </a>
          <button onClick={() => { setDone(null); resetTx(); }} className="mt-3 block w-full rounded-xl border border-white/15 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-white/60 hover:bg-white/[0.06]">
            OK
          </button>
        </div>
      ) : !isConnected ? (
        <div className="mt-4" data-testid="burn-connect-panel">
          <p className="text-xs text-white/50">{t("connectToBurn")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {connectors.map((c) => (
              <button key={c.uid} disabled={isConnecting} onClick={() => connect({ connector: c })}
                data-testid={`burn-connect-${c.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-xs font-bold text-white transition-all hover:border-orange-400/60 disabled:opacity-50">
                <Wallet className="h-3.5 w-3.5 text-orange-300" />
                {c.id === "injected" ? "MetaMask / Browser" : c.name}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-2">
            {balanceRbl != null ? (
              <p className="font-mono text-[11px] text-white/55" data-testid="burn-balance">
                {t("yourBalance")}: <span className="font-black text-white">{fmt(balanceRbl)} R₿L</span>
              </p>
            ) : <span />}
            <button onClick={() => disconnect()} data-testid="burn-disconnect-btn"
              className="shrink-0 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-[0.15em] text-white/50 transition-colors hover:border-rose-400/40 hover:text-rose-300">
              {t("disconnect")} · {address?.slice(0, 6)}…{address?.slice(-4)}
            </button>
          </div>
          <label className="mt-3 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{t("amountToBurn")}</label>
          <div className="mt-1 flex gap-2">
            <input type="number" min={MIN_BURN} value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={`≥ ${fmt(MIN_BURN)}`} data-testid="burn-amount-input"
              className="w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 font-mono text-sm font-bold text-white outline-none placeholder:text-white/25 focus:border-orange-400/60" />
            <button onClick={() => balanceRbl != null && setAmount(String(balanceRbl))} data-testid="burn-max-btn"
              className="rounded-xl border border-orange-400/40 bg-orange-400/10 px-4 font-mono text-[10px] font-black uppercase text-orange-300 hover:bg-orange-400/20">
              {t("max")}
            </button>
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <p className="font-mono text-[10px] text-white/35">{t("minBurn")}</p>
            {usd && <p className="font-mono text-[11px] text-white/55" data-testid="burn-usd-value">≈ ${usd} USD</p>}
          </div>
          <p className="mt-1 text-[10px] text-white/35">{t("foreverNote")}</p>
          {insufficient && <p className="mt-1 text-[11px] font-bold text-rose-300" data-testid="burn-insufficient">{t("insufficientBalance")}</p>}

          {wrongNetwork ? (
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-[11px] font-bold text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("wrongNet")}
              </span>
              <button onClick={() => switchChain({ chainId: BASE_CHAIN_ID })} disabled={isSwitching}
                className="rounded-full bg-amber-400 px-3 py-1.5 text-[11px] font-black uppercase text-black disabled:opacity-50">
                {t("switchBase")}
              </button>
            </div>
          ) : !user ? (
            <button onClick={openLogin} data-testid="burn-login-btn"
              className="mt-3 w-full rounded-xl border border-orange-400/40 bg-orange-400/10 py-3 text-xs font-black uppercase tracking-[0.18em] text-orange-200 hover:bg-orange-400/20">
              {t("loginFirst")}
            </button>
          ) : (
            <button onClick={() => setConfirming(true)} disabled={!canBurn} data-testid="burn-submit-btn"
              className="mt-3 w-full rounded-xl bg-gradient-to-r from-orange-400 to-rose-500 py-3 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-40">
              {busyLabel ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {busyLabel}</span>
              ) : (
                <>🔥 {t("burnButton")}</>
              )}
            </button>
          )}
          <p className="mt-2 text-center font-mono text-[10px] text-rose-300/70">⚠️ {t("irreversibleWarning")}</p>
          {txError && (
            <p className="mt-2 break-words rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[11px] text-rose-300" data-testid="burn-tx-error">
              {txError.shortMessage || txError.message}
            </p>
          )}
          {recordError && txHash && (
            <p className="mt-2 break-words rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-300" data-testid="burn-record-error">
              {recordError} <span className="font-mono">{txHash}</span>
            </p>
          )}
        </div>
      )}

      <HallOfFlame board={board} t={t} />

      {board?.myRank && !done && (
        <p className="mt-3 font-mono text-[11px] text-orange-300" data-testid="burn-my-rank">
          {t("rank")}: #{board.myRank} · {t("burnedAmount")}: {fmt(Number(board.myTotalBurned))} R₿L
        </p>
      )}

      {confirming && (
        <ConfirmBurnModal amount={amt} usd={usd} t={t} onCancel={() => setConfirming(false)} onConfirm={executeBurn} />
      )}
    </div>
  );
}

const queryClient = new QueryClient();

export default function VoluntaryBurnPanel() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <BurnInner />
      </QueryClientProvider>
    </WagmiProvider>
  );
}
